// engine/index.js
import { utilitiesRegistry } from './utilities'

class ModuleCache {
  constructor() {
    this.functionCache = new Map()
    // Initialize with static registries
    this.initializeRegistries()
  }

  initializeRegistries() {
    // Register utilities
    Object.entries(utilitiesRegistry).forEach(([path, func]) => {
      this.functionCache.set(`utilities/${path}`, func)
    })

    console.log('Initialized registries with functions:', Array.from(this.functionCache.keys()))
  }

  getFunction(functionPath) {
    // Check function cache (registry)
    if (this.functionCache.has(functionPath)) {
      console.log(`✅ Found function '${functionPath}' in registry`)
      return this.functionCache.get(functionPath)
    }

    // Function not found
    const pathParts = functionPath.split('/')
    if (pathParts.length !== 3) {
      throw new Error(`Invalid function path format: ${functionPath}. Expected format: 'folder/file/function'`)
    }

    const [folderName] = pathParts
    const availableFunctions = Array.from(this.functionCache.keys())
      .filter(key => key.startsWith(folderName))

    throw new Error(
      `Function '${functionPath}' not found in registry. ` +
      `Available ${folderName} functions: ${availableFunctions.join(', ') || 'none'}. ` +
      `Make sure to add it to the registry in engine/${folderName}/registry.js`
    )
  }

  executeFunction(functionPath, data = {}, options = {}) {
    const {
      throwOnError = true,
      defaultReturn = null,
      silent = false
    } = options

    if (!functionPath) {
      const error = new Error('Function path is required')
      if (throwOnError) throw error
      if (!silent) console.warn('Module execution failed:', error.message)
      return defaultReturn
    }

    try {
      const targetFunction = this.getFunction(functionPath)
      return targetFunction(data)
    } catch (error) {
      const fullError = new Error(`Failed to execute function '${functionPath}': ${error.message}`)

      if (throwOnError) {
        throw fullError
      }

      if (!silent) {
        console.warn('Module execution failed:', fullError.message)
      }

      return defaultReturn
    }
  }

  // Add method to register functions at runtime
  registerFunction(functionPath, func) {
    if (typeof func !== 'function') {
      throw new Error(`Expected function, got ${typeof func}`)
    }
    this.functionCache.set(functionPath, func)
    console.log(`Registered function: ${functionPath}`)
  }

  // Get all registered functions for a specific folder
  getRegisteredFunctions(folderName) {
    const functions = {}
    for (const [path, func] of this.functionCache.entries()) {
      if (path.startsWith(`${folderName}/`)) {
        functions[path] = func
      }
    }
    return functions
  }

  clearCache() {
    this.functionCache.clear()
    this.initializeRegistries() // Re-initialize registries
  }

  getCacheStats() {
    const registryCount = Object.keys(utilitiesRegistry).length

    return {
      totalFunctions: this.functionCache.size,
      registryFunctions: registryCount,
      functionKeys: Array.from(this.functionCache.keys())
    }
  }
}

const moduleCache = new ModuleCache()

// Now all functions are SYNCHRONOUS!
export const executeModule = (functionPath, data = {}, options = {}) =>
  moduleCache.executeFunction(functionPath, data, options)

export const executeModuleSafe = (functionPath, data = {}, defaultReturn = null) =>
  moduleCache.executeFunction(functionPath, data, {
    throwOnError: false,
    silent: true,
    defaultReturn
  })

export const executeModuleWithWarning = (functionPath, data = {}, defaultReturn = null) =>
  moduleCache.executeFunction(functionPath, data, {
    throwOnError: false,
    silent: false,
    defaultReturn
  })

// Runtime registration
export const registerFunction = (functionPath, func) => moduleCache.registerFunction(functionPath, func)

// Get registered functions
export const getRegisteredFunctions = (folderName) => moduleCache.getRegisteredFunctions(folderName)

// Cache management
export const clearModuleCache = () => moduleCache.clearCache()
export const getModuleCacheStats = () => moduleCache.getCacheStats()

export { ModuleCache }